import { useQuery } from '@tanstack/react-query'
import { ArrowRight, GraduationCap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAuth } from '@/features/auth/useAuth'
import { classRoomsQuery, useOpenClassRoom } from '@/features/messaging/api'
import { messagesBase } from '@/features/messaging/format'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** Every class room of the school (managers) or of my classes (teachers), opened on demand. */
export function ClassRoomsPage() {
  const { t } = useTranslation('messaging')
  useDocumentTitle('Class rooms')
  const { portal } = useAuth()
  const navigate = useNavigate()
  const rooms = useQuery(classRoomsQuery())
  const open = useOpenClassRoom()
  const base = messagesBase(portal)

  return (
    <>
      <PageHeader
        title={t('Class rooms')}
        description={t(
          'One room per class: its students and the teachers who teach it. Teachers can start a live class from the room.',
        )}
        breadcrumbs={[{ label: t('Messages'), to: base }, { label: t('Class rooms') }]}
        homeHref={portal === 'staff' ? '/staff' : '/admin'}
      />
      {rooms.isPending ? (
        <LoadingState />
      ) : rooms.error ? (
        <ErrorState onRetry={() => void rooms.refetch()} />
      ) : rooms.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<GraduationCap />}
            title={t('No classes')}
            description={t(
              'Class rooms appear once classes have sections (and, for teachers, once you teach one).',
            )}
          />
        </Card>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rooms.data.map((r) => (
            <li key={r.section.id}>
              <button
                type="button"
                disabled={open.isPending}
                onClick={() =>
                  open.mutate(r.section.id, {
                    onSuccess: (c) => void navigate(`${base}/${c.id}`),
                    onError: (e) => toast.error(t('Could not open the class room'), toApiError(e).message),
                  })
                }
                className="group flex w-full items-center gap-3 rounded-card border border-line bg-surface p-4 text-left shadow-card transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-raised"
              >
                <span
                  className="grid size-10 place-items-center rounded-full bg-brand-100 text-brand-700"
                  aria-hidden="true"
                >
                  <GraduationCap className="size-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-ink group-hover:text-brand-700">
                    {r.section.label}
                  </span>
                  <span className="block text-xs text-muted">
                    {r.conversationId ? t('Open the room') : t('Not opened yet')}
                  </span>
                </span>
                <ArrowRight className="size-4 text-subtle group-hover:text-brand-600" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
